import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  ArrowLeft, 
  Loader2, 
  CreditCard, 
  CheckCircle,
  AlertTriangle,
  Clock,
  XCircle,
  RefreshCw,
  Banknote,
  Car,
  Timer,
  Receipt,
  Smartphone,
  ChevronRight,
  Phone,
  Wifi
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { KioskViewport } from "../../components/ui/kiosk-viewport";
import { KioskHeader } from "../../components/ui/kiosk-header";

const API = '/api';
const IDLE_TIMEOUT = 60;
const POLL_INTERVAL = 5000; // Poll every 5 seconds for payment status

// Card Reader Instruction Component - HIGH VISIBILITY - FULL WIDTH
const CardReaderInstruction = () => (
  <div style={{
    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    border: '4px solid #f59e0b',
    borderRadius: '20px',
    padding: '24px 32px',
    marginBottom: '24px',
    boxShadow: '0 8px 32px rgba(245, 158, 11, 0.3)'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
      {/* Directional arrows */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <ChevronRight style={{ width: '60px', height: '60px', color: '#b45309' }} />
        <ChevronRight style={{ width: '60px', height: '60px', color: '#b45309', marginLeft: '-30px' }} />
      </div>
      
      {/* Instructions */}
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: '32px', fontWeight: '900', color: '#78350f', marginBottom: '8px', fontFamily: 'Outfit, sans-serif' }}>
          CARD READER ON YOUR RIGHT
        </p>
        <p style={{ fontSize: '22px', color: '#92400e', fontWeight: '600', margin: 0 }}>
          Tap contactless or insert chip & PIN
        </p>
      </div>
      
      {/* Payment icons */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Wifi style={{ width: '44px', height: '44px', color: '#b45309', transform: 'rotate(90deg)' }} />
          <CreditCard style={{ width: '52px', height: '52px', color: '#b45309' }} />
        </div>
        <span style={{ color: '#92400e', fontSize: '14px', fontWeight: '700' }}>TAP OR INSERT</span>
      </div>
    </div>
  </div>
);

export default function KioskSessionPayment() {
  const navigate = useNavigate();
  const location = useLocation();
  const { vehicleReg } = location.state || {};
  // DO NOT use sessionData from navigation - always fetch fresh
  
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null); // Always start with null, fetch fresh
  const [paymentState, setPaymentState] = useState("idle"); // idle, processing, success, failed
  const [error, setError] = useState(null);
  const idleTimerRef = useRef(null);
  const pollRef = useRef(null);
  
  // DEBUG: Store raw API response for render-path proof
  const [debugApiResponse, setDebugApiResponse] = useState(null);
  const JS_BUILD = "e8ef33d1";

  // Idle detection
  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [navigate]);

  // Fetch session data from backend - ALWAYS fetch fresh, ignore stale navigation state
  const fetchSession = useCallback(async () => {
    if (!vehicleReg) {
      navigate("/kiosk/pay");
      return;
    }

    // Clear any stale state before fetching
    setSession(null);
    setLoading(true);
    setError(null);

    try {
      // Use session calculate endpoint for accurate real-time charges
      const response = await axios.get(`${API}/parking/session/${encodeURIComponent(vehicleReg)}/calculate`);
      
      // DEBUG: Store raw API response
      setDebugApiResponse(response.data);

      if (response.data.session_id) {
        // Map response to session format, using amount_due as canonical amount
        const freshSession = {
          session_id: response.data.session_id,
          vehicle_registration: response.data.vehicle_registration,
          site_id: response.data.site_id,
          entry_time: response.data.entry_time,
          duration_hours: response.data.duration_hours,
          duration_display: response.data.duration_display,
          amount_due: response.data.amount_due,
          // Use backend outstanding if provided, otherwise amount_due is the outstanding
          outstanding: response.data.outstanding ?? response.data.amount_due,
          amount_paid: response.data.amount_paid ?? 0,
          has_exit_auth: response.data.has_exit_auth ?? false,
          is_grace_period: response.data.is_grace_period ?? false,
          has_entitlement: response.data.has_entitlement ?? false,
          entitlement_discount: response.data.entitlement_discount ?? 0,
          // P1 UX: Whitelist status for display
          is_whitelisted: response.data.is_whitelisted ?? false,
          whitelist_reason: response.data.whitelist_reason ?? null
        };
        setSession(freshSession);
      } else {
        // No session_id in response - genuine no-session outcome
        setError("Free parking for you today.");
        setSession(null);
      }
    } catch (err) {
      console.error("Session lookup error:", err);
      if (err.response?.status === 404) {
        // Genuine no-session outcome (404 = session not found)
        setError("Free parking for you today.");
      } else {
        // API/network error - keep technical message
        setError("Unable to retrieve session. Please try again.");
      }
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [vehicleReg, navigate]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Format duration
  const formatDuration = (hours) => {
    if (!hours) return "0h 0m";
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "£0.00";
    return `£${parseFloat(amount).toFixed(2)}`;
  };

  // Format entry time
  const formatEntryTime = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle payment initiation via Stripe Terminal
  const handleInitiatePayment = async () => {
    if (!session?.session_id) {
      setError("No session found. Please try again.");
      return;
    }
    
    // Check if payment is actually needed - use amount_due as canonical source
    const amountDue = session?.amount_due ?? 0;
    if (amountDue <= 0) {
      // No payment needed - session is free or already paid
      setPaymentState("success");
      toast.success("No payment required!");
      return;
    }

    setPaymentState("processing");
    setError(null);
    
    try {
      // Create Stripe Terminal payment intent via backend
      const response = await axios.post(`${API}/terminal/intent`, {
        session_id: session.session_id
      });

      if (response.data.payment_intent_id) {
        // Terminal payment initiated successfully
        toast.info("Payment initiated - please complete on card terminal");
        
        const paymentIntentId = response.data.payment_intent_id;
        
        // Start polling for payment completion
        pollRef.current = setInterval(async () => {
          try {
            // Poll terminal status endpoint
            const statusRes = await axios.get(`${API}/terminal/status/${paymentIntentId}`);
            
            if (statusRes.data.is_settled && statusRes.data.has_exit_authorisation) {
              clearInterval(pollRef.current);
              setPaymentState("success");
              // Refresh session data with proper mapping
              const sessionRes = await axios.get(`${API}/parking/session/${encodeURIComponent(vehicleReg)}/calculate`);
              if (sessionRes.data.session_id) {
                const refreshedSession = {
                  session_id: sessionRes.data.session_id,
                  vehicle_registration: sessionRes.data.vehicle_registration,
                  site_id: sessionRes.data.site_id,
                  entry_time: sessionRes.data.entry_time,
                  duration_hours: sessionRes.data.duration_hours,
                  duration_display: sessionRes.data.duration_display,
                  amount_due: sessionRes.data.amount_due,
                  outstanding: sessionRes.data.outstanding ?? sessionRes.data.amount_due,
                  amount_paid: sessionRes.data.amount_paid ?? 0,
                  has_exit_auth: sessionRes.data.has_exit_auth ?? true,
                  is_grace_period: sessionRes.data.is_grace_period ?? false,
                  has_entitlement: sessionRes.data.has_entitlement ?? false,
                  entitlement_discount: sessionRes.data.entitlement_discount ?? 0,
                  // P1 UX: Whitelist status for display
                  is_whitelisted: sessionRes.data.is_whitelisted ?? false,
                  whitelist_reason: sessionRes.data.whitelist_reason ?? null
                };
                setSession(refreshedSession);
              }
            }
          } catch (e) {
            console.error("Poll error:", e);
          }
        }, POLL_INTERVAL);

        // Timeout after 2 minutes
        setTimeout(() => {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            if (paymentState === "processing") {
              setPaymentState("failed");
              setError("Payment timed out. Please try again.");
            }
          }
        }, 120000);

      } else if (response.data.detail) {
        // Already settled or no payment needed
        const detail = response.data.detail;
        if (detail.includes("already settled") || detail.includes("No payment required") || detail.includes("No outstanding")) {
          setPaymentState("success");
          toast.success("No payment required - session is free!");
        } else {
          setPaymentState("failed");
          setError(detail);
        }
      }
    } catch (err) {
      console.error("Payment error:", err);
      setPaymentState("failed");
      const errorMsg = err.response?.data?.detail || "Payment failed. Please try again.";
      setError(typeof errorMsg === 'string' ? errorMsg : errorMsg.message || "Payment failed. Please try again.");
    }
  };

  // Request exit authorization after successful payment
  const handleRequestExit = async () => {
    if (!session?.session_id) return;

    try {
      await axios.post(`${API}/parking/authorise-exit`, {
        session_id: session.session_id
      });
      toast.success("Exit authorized! You may now leave the car park.");
      navigate("/kiosk/confirmation", { 
        state: { 
          type: "payment_success",
          vehicleReg,
          amountPaid: session.amount_due
        }
      });
    } catch (err) {
      console.error("Exit auth error:", err);
      // Even if exit auth fails, payment was successful
      navigate("/kiosk/confirmation", { 
        state: { 
          type: "payment_success",
          vehicleReg,
          amountPaid: session.amount_due
        }
      });
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div 
        data-testid="kiosk-session-payment"
        className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center"
      >
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-white text-xl">Loading session details...</p>
        </div>
      </div>
    );
  }

  // Render error state - distinguish between no-session (free parking) and technical errors
  if (error && !session) {
    const isFreeParking = error === "Free parking for you today.";
    return (
      <div 
        data-testid="kiosk-session-payment"
        className={`min-h-screen bg-gradient-to-br ${isFreeParking ? 'from-green-900 to-slate-800' : 'from-slate-900 to-slate-800'} p-8`}
      >
        <div className="max-w-2xl mx-auto">
          <div className={`${isFreeParking ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'} border rounded-2xl p-8 text-center`}>
            {isFreeParking ? (
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            ) : (
              <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            )}
            <h2 className="text-2xl font-bold text-white mb-2">
              {isFreeParking ? "Free parking for you today." : "Session Not Found"}
            </h2>
            {!isFreeParking && <p className="text-red-300 mb-6">{error}</p>}
            <button
              onClick={() => navigate("/kiosk/pay")}
              className="bg-slate-700 hover:bg-slate-600 text-white px-6 py-3 rounded-xl font-medium"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5 inline mr-2" />
              {isFreeParking ? "Check Another Vehicle" : "Try Another Registration"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render payment success
  if (paymentState === "success") {
    return (
      <div 
        data-testid="kiosk-session-payment"
        className="min-h-screen bg-gradient-to-br from-green-900 to-slate-800 p-8"
      >
        <div className="max-w-2xl mx-auto">
          <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-8 text-center">
            <CheckCircle className="w-20 h-20 text-green-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-2">Payment Successful!</h2>
            <p className="text-green-300 text-xl mb-8">Thank you for your payment</p>
            
            <div className="bg-slate-800/50 rounded-xl p-6 mb-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Car className="w-8 h-8 text-blue-400" />
                <span className="text-2xl font-mono font-bold text-white">{vehicleReg}</span>
              </div>
              <p className="text-slate-400">Your exit is now authorized</p>
            </div>

            <button
              onClick={handleRequestExit}
              className="w-full bg-green-500 hover:bg-green-600 text-white py-4 rounded-xl font-bold text-xl"
              data-testid="exit-button"
            >
              <CheckCircle className="w-6 h-6 inline mr-2" />
              Proceed to Exit
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main payment view
  return (
    <KioskViewport>
      <div 
        data-testid="kiosk-session-payment"
        style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: '#0d2137' }}
      >
        {/* STANDARDIZED HEADER */}
        <KioskHeader showDateTime={true} />

        <main style={{ flex: 1, overflow: 'auto', padding: '24px 32px', background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)' }}>
          {/* BACK BUTTON - TOP OF CONTENT AREA */}
          <button
            onClick={() => navigate("/kiosk/pay")}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 24px',
              borderRadius: '14px',
              backgroundColor: '#1e3a5f',
              border: 'none',
              cursor: 'pointer',
              marginBottom: '24px'
            }}
            data-testid="back-button"
          >
            <ArrowLeft style={{ width: '28px', height: '28px', color: 'white' }} />
            <span style={{ color: 'white', fontWeight: '700', fontSize: '20px', fontFamily: 'Outfit, sans-serif' }}>Back</span>
          </button>

          {/* DEBUG: Render-path proof - TEMPORARY */}
          <div style={{ 
            background: '#ef4444', 
            border: '3px solid #fbbf24', 
            borderRadius: '12px', 
            padding: '16px', 
            marginBottom: '24px',
            fontFamily: 'monospace',
            fontSize: '14px',
            color: 'white'
          }} data-testid="debug-panel">
            <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#fbbf24' }}>DEBUG PANEL</div>
            <div>JS_BUILD = {JS_BUILD}</div>
            <div>API_duration_display = {debugApiResponse?.duration_display ?? 'null'}</div>
            <div>API_amount_due = {debugApiResponse?.amount_due ?? 'null'}</div>
            <div>RENDERED_duration = {session?.duration_display || formatDuration(session?.duration_hours)}</div>
            <div>RENDERED_amount = {session?.amount_due ?? 'null'}</div>
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#94a3b8' }}>
              session.duration_display = {session?.duration_display ?? 'undefined'} | 
              session.duration_hours = {session?.duration_hours ?? 'undefined'}
            </div>
          </div>

          {/* Vehicle Info - FULL WIDTH */}
          <div style={{ background: '#facc15', borderRadius: '16px', padding: '20px 32px', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
              <div style={{ background: '#1e3a8a', color: 'white', padding: '6px 12px', borderRadius: '6px', fontWeight: '700', fontSize: '18px' }}>GB</div>
              <span style={{ fontSize: '48px', fontFamily: 'monospace', fontWeight: '700', color: '#0f172a', letterSpacing: '4px' }}>
                {vehicleReg}
              </span>
            </div>
          </div>

          {/* Session Details Card - FULL WIDTH */}
          <div style={{ background: 'rgba(30, 41, 59, 0.8)', border: '2px solid #334155', borderRadius: '20px', padding: '24px 32px', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '22px', fontWeight: '600', color: 'white', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px', fontFamily: 'Outfit, sans-serif' }}>
              <Receipt style={{ width: '24px', height: '24px', color: '#3b82f6' }} />
              Session Details
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid #334155' }}>
                <span style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '18px' }}>
                  <Clock style={{ width: '20px', height: '20px' }} />
                  Entry Time
                </span>
                <span style={{ color: 'white', fontWeight: '500', fontSize: '20px' }}>{formatEntryTime(session?.entry_time)}</span>
              </div>

              {/* P1 UX: Show Parking Status for whitelisted, Duration for others */}
              {session?.is_whitelisted ? (
                <div style={{ display: 'flex', flexDirection: 'column', padding: '16px 0', borderBottom: '1px solid #334155' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '18px' }}>
                      <CheckCircle style={{ width: '20px', height: '20px', color: '#22c55e' }} />
                      Parking Status
                    </span>
                    <span style={{ color: '#22c55e', fontWeight: '600', fontSize: '20px' }}>No payment required</span>
                  </div>
                  <p style={{ color: '#64748b', fontSize: '14px', marginTop: '8px', marginLeft: '30px' }}>
                    This vehicle is registered for free parking at this location.
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid #334155' }}>
                  <span style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '18px' }}>
                    <Timer style={{ width: '20px', height: '20px' }} />
                    Duration
                  </span>
                  <span style={{ color: 'white', fontWeight: '500', fontSize: '20px' }}>{session?.duration_display || formatDuration(session?.duration_hours)}</span>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid #334155' }}>
                <span style={{ color: '#94a3b8', fontSize: '18px' }}>Total Charge</span>
                <span style={{ color: 'white', fontWeight: '500', fontSize: '20px' }}>{formatCurrency(session?.amount_due)}</span>
              </div>

              {session?.amount_paid > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid #334155' }}>
                  <span style={{ color: '#22c55e', fontSize: '18px' }}>Amount Paid</span>
                  <span style={{ color: '#22c55e', fontWeight: '500', fontSize: '20px' }}>-{formatCurrency(session?.amount_paid)}</span>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', background: 'rgba(15, 23, 42, 0.6)', borderRadius: '14px', marginTop: '8px' }}>
                <span style={{ color: 'white', fontWeight: '600', fontSize: '22px', fontFamily: 'Outfit, sans-serif' }}>Amount Due</span>
                <span style={{ fontSize: '36px', fontWeight: '700', color: '#22c55e', fontFamily: 'Outfit, sans-serif' }}>
                  {formatCurrency(session?.amount_due)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Status */}
          {session?.amount_due === 0 || session?.has_exit_auth ? (
            <div style={{ background: 'rgba(34, 197, 94, 0.1)', border: '2px solid rgba(34, 197, 94, 0.3)', borderRadius: '20px', padding: '32px', textAlign: 'center' }}>
              <CheckCircle style={{ width: '64px', height: '64px', color: '#22c55e', margin: '0 auto 16px' }} />
              <h3 style={{ fontSize: '26px', fontWeight: '700', color: 'white', marginBottom: '12px', fontFamily: 'Outfit, sans-serif' }}>No Payment Required</h3>
              <p style={{ color: '#86efac', marginBottom: '24px', fontSize: '18px' }}>Your session is already paid or within free period</p>
              <button
                onClick={handleRequestExit}
                style={{ background: '#22c55e', color: 'white', padding: '16px 48px', borderRadius: '14px', border: 'none', fontWeight: '700', fontSize: '22px', cursor: 'pointer' }}
                data-testid="exit-button"
              >
                Proceed to Exit
              </button>
            </div>
          ) : paymentState === "processing" ? (
            <div style={{ background: 'rgba(59, 130, 246, 0.1)', border: '2px solid rgba(59, 130, 246, 0.3)', borderRadius: '20px', padding: '32px', textAlign: 'center' }}>
              <CardReaderInstruction />
              <Loader2 style={{ width: '64px', height: '64px', color: '#3b82f6', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
              <h3 style={{ fontSize: '24px', fontWeight: '700', color: 'white', marginBottom: '12px', fontFamily: 'Outfit, sans-serif' }}>Processing Payment</h3>
              <p style={{ color: '#93c5fd', fontSize: '18px' }}>Please complete payment on the card terminal</p>
            </div>
          ) : paymentState === "failed" ? (
            <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '2px solid rgba(239, 68, 68, 0.3)', borderRadius: '20px', padding: '32px', textAlign: 'center' }}>
              <AlertTriangle style={{ width: '64px', height: '64px', color: '#ef4444', margin: '0 auto 16px' }} />
              <h3 style={{ fontSize: '24px', fontWeight: '700', color: 'white', marginBottom: '12px', fontFamily: 'Outfit, sans-serif' }}>Payment Failed</h3>
              <p style={{ color: '#fca5a5', marginBottom: '24px', fontSize: '18px' }}>{error || "Please try again"}</p>
              <button
                onClick={() => { setPaymentState("idle"); setError(null); }}
                style={{ background: '#475569', color: 'white', padding: '14px 32px', borderRadius: '12px', border: 'none', fontWeight: '600', fontSize: '18px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '10px' }}
              >
                <RefreshCw style={{ width: '22px', height: '22px' }} />
                Try Again
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Card Reader Instruction - TOP PRIORITY */}
              <CardReaderInstruction />
              
              {/* Pay by QR option */}
              <button
                onClick={() => navigate("/kiosk/pay-qr", { 
                  state: { 
                    sessionId: session.session_id, 
                    vehicleReg: vehicleReg,
                    amount: session.amount_due
                  } 
                })}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  color: 'white',
                  padding: '24px 32px',
                  borderRadius: '18px',
                  border: 'none',
                  fontWeight: '700',
                  fontSize: '24px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '14px',
                  boxShadow: '0 8px 24px rgba(139, 92, 246, 0.4)'
                }}
                data-testid="pay-qr-button"
              >
                <Smartphone style={{ width: '32px', height: '32px' }} />
                Pay by Phone (QR Code)
              </button>
              
              {/* Pay at terminal option */}
              <button
                onClick={handleInitiatePayment}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  color: 'white',
                  padding: '24px 32px',
                  borderRadius: '18px',
                  border: 'none',
                  fontWeight: '700',
                  fontSize: '24px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '14px',
                  boxShadow: '0 8px 24px rgba(34, 197, 94, 0.4)'
                }}
                data-testid="pay-before-exit-button"
              >
                <Banknote style={{ width: '32px', height: '32px' }} />
                Pay Before Exit {formatCurrency(session?.amount_due)}
              </button>
              
              {/* HELP SECTION */}
              <div style={{ marginTop: '16px', padding: '20px 24px', background: '#1e3a5f', borderRadius: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '14px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
                  <Phone style={{ width: '24px', height: '24px', color: '#fbbf24' }} />
                  <span style={{ color: 'white', fontWeight: '600', fontSize: '18px', fontFamily: 'Outfit, sans-serif' }}>Need help? Call </span>
                  <span style={{ color: '#fbbf24', fontWeight: '700', fontSize: '22px', fontFamily: 'Outfit, sans-serif' }}>01292 833107</span>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
      
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </KioskViewport>
  );
}
